import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { assignOrder, getAllOrders } from '../../redux/reduxSlices/ordersSlice';
import { CheckCircle, ChevronRight, Clock, FileCheck, FileText, Filter, Music, Music2, Package, Search, Truck, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import { getMusicCreators } from '../../redux/reduxSlices/userProfileSlice';

const OrdersTable = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  const navigate = useNavigate();
  const dispatch = useDispatch()
  const {
    loading,
    fetchError,
    ordersData,
  } = useSelector((state) => state.orders);
  const { musicCreators } = useSelector((state) => state.profile);


  useEffect(() => {
    // dispatch(getUsersList())
    dispatch(getAllOrders());
    dispatch(getMusicCreators());
  }, [])

  const assignTask = async (orderId, assigned_to) => {
    try {
      const payload = {
        order_id: orderId,
        user_pk: assigned_to
      };
      const response = await dispatch(assignOrder(payload)).unwrap();
      if (response.success) {
        await dispatch(getAllOrders());
        toast.success("Task assigned successfully");
      }
    } catch (error) {
      console.log("error", error);
      toast.error(error?.message || "Failed to assign task.");
    }
  };

  const filteredOrders = (ordersData || []).filter((order) => {
    const search = searchTerm.toLowerCase();

    const companyName = order.company_name?.toLowerCase().trim() || "";
    const contactPerson = order.contact_person?.toLowerCase().trim() || "";
    const orderId = order.order_id?.toLowerCase().trim() || "";
    const email = order.email?.toLowerCase().trim() || "";

    const matchesSearch =
      companyName.includes(search) ||
      contactPerson.includes(search) ||
      orderId.includes(search) ||
      email.includes(search);

    const matchesStatus =
      filterStatus === "all" || order.status === filterStatus;

    return matchesSearch && matchesStatus;
  });

  const statusConfig = [
    { value: "pending", label: "Pending", color: "bg-yellow-500", gradient: "from-yellow-400 to-yellow-600", icon: Clock },
    { value: "in_progress", label: "In Progress", color: "bg-blue-500", gradient: "from-blue-400 to-blue-600", icon: Music },
    { value: "lyrics_approval_pending", label: "Lyrics Approval Pending", color: "bg-orange-500", gradient: "from-orange-400 to-orange-600", icon: FileText },
    { value: "lyrics_approved", label: "Lyrics Approved", color: "bg-purple-500", gradient: "from-purple-400 to-purple-600", icon: FileCheck },
    { value: "music_approval_pending", label: "Music Approval Pending", color: "bg-indigo-500", gradient: "from-indigo-400 to-indigo-600", icon: Music2 },
    { value: "music_approved", label: "Music Approved", color: "bg-teal-500", gradient: "from-teal-400 to-teal-600", icon: CheckCircle },
    { value: "delivered", label: "Delivered", color: "bg-green-500", gradient: "from-green-400 to-green-600", icon: Truck }
  ];


  const StatusBadge = ({ status }) => {
    const config = statusConfig.find(s => s.value === status);
    const Icon = config?.icon || Clock;

    return (
      <div className={`px-2 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${config?.color ? `bg-${config.color.split('-')[1]}-100 text-${config.color.split('-')[1]}-800` : 'bg-gray-100 text-gray-800'
        }`}>
        <Icon className="w-3 h-3" />
        {config?.label || status}
      </div>
    );
  };

  const SimpleArrowTimeline = () => {
    return (
      <div className="mb-4">
        <div className="flex items-center justify-center">
          <div className="w-full sm:w-auto">
            {/* Mobile: 2 columns grid */}
            <div className="sm:hidden grid grid-cols-2 gap-4">
              {statusConfig.map((status, index) => (
                <div key={status.value} className="flex flex-col items-center">
                  <div className={`px-3 py-2 rounded-md border text-xs font-medium text-center w-full ${status.color.replace('bg-', 'border-')} ${status.color.replace('bg-', 'bg-')} bg-opacity-10`}>
                    {status.label}
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop/Tablet: Horizontal flow with arrows */}
            <div className="hidden sm:flex items-center justify-center">
              {statusConfig.map((status, index) => (
                <React.Fragment key={status.value}>
                  {/* Status Box */}
                  <div className="flex flex-col items-center min-w-[100px]">
                    <div className={`px-3 py-2 rounded-md border text-xs font-medium text-center ${status.color.replace('bg-', 'border-')} ${status.color.replace('bg-', 'bg-')} bg-opacity-10`}>
                      {status.label}
                    </div>
                  </div>

                  {/* Arrow between boxes (except after last one) */}
                  {index < statusConfig.length - 1 && (
                    <div className="flex items-center mx-1">
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                      <ChevronRight className="w-4 h-4 text-gray-400 -ml-2" />
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };




  const handleBack = () => {
    navigate(-1);
  };



  return (
    <div className="min-h-screen p-6">
      <div className="max-w-[1800px] mx-auto">
        {/* Header Section */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Order Details</h1>
            <p className="text-gray-600 mt-2">View and manage all client orders</p>
          </div>
          <button
            onClick={handleBack}
            className="bg-gradient-to-br from-orange-500 to-orange-600 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors duration-300 "
          >
            Back to Dashboard
          </button>
        </div>

        {/* Search and Filter Section - Compact */}
        <div className="bg-white rounded-lg p-4 shadow border border-gray-200 mb-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-3">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Client Orders</h2>
              <p className="text-sm text-gray-600">{filteredOrders.length} orders found</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search orders..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full py-2 pl-10 pr-3 bg-gray-50 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="py-2 px-3 bg-gray-50 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 min-w-[160px]"
              >
                <option value="all">All Status</option>
                {statusConfig.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <SimpleArrowTimeline />

        {/* Orders Table */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="bg-white rounded-lg shadow border border-gray-200">
            {loading ? (
              <div className="p-8 text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg mb-3">
                  <Package className="w-6 h-6 text-white animate-pulse" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                  Loading orders...
                </h3>
                <p className="text-gray-600 text-sm">Please wait</p>
              </div>
            ) : filteredOrders.length === 0 ? (
              <div className="text-center py-12 px-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-lg mb-4">
                  <Users className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  No orders found
                </h3>
                <p className="text-gray-600 text-sm">Try adjusting your search or filters</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        #
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Order ID
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Company
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contact
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Assign to
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredOrders.map((order, index) => (
                      <tr key={order.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {index + 1}
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-gray-900">
                            {order.order_id || "-"}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-gray-900 max-w-[120px] truncate">
                            {order.company_name || order.brand_company_name || "-"}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm text-gray-900 max-w-[100px] truncate" title={order.contact_person || "-"}>
                            {(order.contact_person && order.contact_person.length > 20)
                              ? `${order.contact_person.substring(0, 20)}...`
                              : (order.contact_person || "-")}
                          </div>
                          <div className="text-xs text-gray-500">
                            {(order.mobile_number && order.mobile_number.length > 20)
                              ? `${order.mobile_number.substring(0, 20)}...`
                              : (order.mobile_number || "-")}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 max-w-[150px] truncate" title={order.email || "-"}>
                          {order.email || "-"}
                        </td>
                        <td className="px-4 py-3">
                          <select
                            value={order.assigned_to || ""}
                            onChange={(e) => assignTask(order.order_id, e.target.value)}
                            className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white w-full"
                          >
                            <option value="" disabled>Assign to</option>
                            {musicCreators?.data?.map((creator) => (
                              <option key={creator.user_pk} value={creator.user_pk}>
                                {creator.first_name} {creator.last_name}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge status={order.status} />
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => navigate(`/my-orders/${order.order_id}`)}
                            className="inline-flex items-center px-3 py-1 text-sm bg-orange-600 text-white rounded hover:bg-orange-700"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Table Footer */}
          {/* <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200">
            <p className="text-sm text-gray-600">
              Showing {orderDetailsData.length} orders
            </p>
            <div className="flex space-x-2">
              <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-300">
                Previous
              </button>
              <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-300">
                Next
              </button>
            </div>
          </div> */}
        </div>
      </div>
    </div>
  );
};

export default OrdersTable;